// Touch gesture hooks for mobile frame navigation
import { useRef, useCallback, useEffect } from 'react'

interface TouchGestureOptions {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeUp?: () => void
  onSwipeDown?: () => void
  threshold?: number // minimum distance for swipe
  restraint?: number // maximum perpendicular distance
  allowedTime?: number // maximum time for swipe
  enabled?: boolean
}

interface TouchState {
  startX: number
  startY: number
  startTime: number
}

export function useTouchGestures(
  ref: React.RefObject<HTMLElement | null>,
  options: TouchGestureOptions
) {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    restraint = 100,
    allowedTime = 300,
    enabled = true,
  } = options

  const touchState = useRef<TouchState | null>(null)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return
    
    const touch = e.touches[0]
    touchState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
    }
  }, [enabled])

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || !touchState.current) return

    const touch = e.changedTouches[0]
    const { startX, startY, startTime } = touchState.current

    const distX = touch.clientX - startX
    const distY = touch.clientY - startY
    const elapsedTime = Date.now() - startTime

    // Check if it's a valid swipe (within time limit)
    if (elapsedTime <= allowedTime) {
      // Horizontal swipe
      if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
        if (distX > 0) {
          onSwipeRight?.()
        } else {
          onSwipeLeft?.()
        }
      }
      // Vertical swipe
      else if (Math.abs(distY) >= threshold && Math.abs(distX) <= restraint) {
        if (distY > 0) {
          onSwipeDown?.()
        } else {
          onSwipeUp?.()
        }
      }
    }

    touchState.current = null
  }, [enabled, threshold, restraint, allowedTime, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown])

  useEffect(() => {
    const element = ref.current
    if (!element || !enabled) return

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [ref, enabled, handleTouchStart, handleTouchEnd])
}

// Hook for pinch-to-zoom gestures
interface PinchGestureOptions {
  onPinchIn?: () => void
  onPinchOut?: () => void
  threshold?: number // minimum scale change
  enabled?: boolean
}

export function usePinchGesture(
  ref: React.RefObject<HTMLElement | null>,
  options: PinchGestureOptions
) {
  const {
    onPinchIn,
    onPinchOut,
    threshold = 0.1,
    enabled = true,
  } = options

  const initialDistance = useRef<number | null>(null)

  const getDistance = (touches: TouchList): number => {
    if (touches.length < 2) return 0
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || e.touches.length !== 2) return
    initialDistance.current = getDistance(e.touches)
  }, [enabled])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || e.touches.length !== 2 || initialDistance.current === null) return

    const currentDistance = getDistance(e.touches)
    const scale = currentDistance / initialDistance.current

    if (scale > 1 + threshold) {
      onPinchOut?.()
      initialDistance.current = currentDistance
    } else if (scale < 1 - threshold) {
      onPinchIn?.()
      initialDistance.current = currentDistance
    }
  }, [enabled, threshold, onPinchIn, onPinchOut])

  const handleTouchEnd = useCallback(() => {
    initialDistance.current = null
  }, [])

  useEffect(() => {
    const element = ref.current
    if (!element || !enabled) return

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: true })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [ref, enabled, handleTouchStart, handleTouchMove, handleTouchEnd])
}
